import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  CircularProgress,
  Alert,
  FormGroup,
  FormControlLabel,
  Checkbox,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stepper,
  Step,
  StepLabel,
  Select,
  MenuItem,
  Snackbar,
  AlertTitle,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Tooltip,
  Card,
  CardContent,
  CardActions,
  Switch,
} from '@mui/material';
import {
  Save,
  Send,
  AutoAwesome,
  Edit,
  Delete,
  Add,
  Upload,
  CheckCircle,
  Error,
  CallSplit,
  DragIndicator,
  Merge,
  OpenInNew,
  Language,
} from '@mui/icons-material';
import { praAPI } from '../services/api';
import SenderAccountSendDialog from './SenderAccountSendDialog';

const CreateRequest = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const draftIdParam = searchParams.get('draft_id');
  const [draftId] = useState(draftIdParam ? parseInt(draftIdParam, 10) : null);
  const [destinations, setDestinations] = useState([]);
  const [activeStep, setActiveStep] = useState(0);

  // Panel 1: Request Overview
  const [formData, setFormData] = useState({
    natural_language_input: '',
    request_context: '',
    destination_ids: [],
    attached_files: [],
  });

  // When true, skip AI topic/email generation and let the user write the
  // letter(s) by hand in Panel 3.
  const [manualMode, setManualMode] = useState(false);

  // Panel 2: Generated Itemized Requests
  const [topics, setTopics] = useState([]);
  const [generatingTopics, setGeneratingTopics] = useState(false);

  // Panel 3: Generated Requests
  const [generatedEmails, setGeneratedEmails] = useState([]);
  const [generatingEmails, setGeneratingEmails] = useState(false);
  const [sentEmailIds, setSentEmailIds] = useState([]); // Track which emails have been sent (by index)
  const [nextRequestDialog, setNextRequestDialog] = useState({
    open: false,
    emailIndex: null,
    nextrequest_id: '',
    nextrequest_url: '',
    submitting: false,
  });

  // Common state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  // Shared send flow: when the user clicks Send we first save the draft, then
  // open the app-wide SenderAccountSendDialog staged with that request id.
  const [sendDialog, setSendDialog] = useState({ open: false, requestId: null, emailIndex: null });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const destResponse = await praAPI.getDestinations();
        setDestinations(destResponse.data);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to load initial data');
      }
    };

    fetchData();
  }, []);

  // If we arrived from the quick-create card on the Requests page, load that draft
  // and prefill Panel 1 so the user continues the same request instead of starting over.
  useEffect(() => {
    if (!draftId) return;
    const loadDraft = async () => {
      try {
        const response = await praAPI.getRequest(draftId);
        const draft = response.data;
        setFormData((prev) => ({
          ...prev,
          natural_language_input: draft.natural_language_input || '',
          destination_ids: draft.destination_id ? [draft.destination_id] : prev.destination_ids,
        }));
      } catch (error) {
        console.error('Error loading draft request:', error);
      }
    };

    loadDraft();
  }, [draftId]);

  // Panel 1: Generate topics from natural language input
  const handleGenerateTopics = async () => {
    if (!formData.natural_language_input.trim()) {
      setError('Please describe your request');
      return;
    }

    if (formData.destination_ids.length === 0) {
      setError('Please select at least one destination');
      return;
    }

    setGeneratingTopics(true);
    setError('');
    setSuccess('');

    try {
      const response = await praAPI.generateTopics({
        natural_language_input: formData.natural_language_input,
        request_context: formData.request_context,
        attached_files: formData.attached_files,
      });

      setTopics(response.data.topics);
      setSuccess(`Generated ${response.data.topics.length} topics!`);
      setActiveStep(1);
    } catch (error) {
      console.error('Error generating topics:', error);
      setError(error.response?.data?.detail || 'Failed to generate topics');
    } finally {
      setGeneratingTopics(false);
    }
  };

  // Panel 1 (manual mode): skip AI and build blank letter(s) directly from the
  // selected destinations so the user writes the body by hand in Panel 3.
  const handleContinueManual = () => {
    if (formData.destination_ids.length === 0) {
      setError('Please select at least one destination');
      return;
    }

    const emails = formData.destination_ids.map((destId) => {
      const dest = destinations.find((d) => d.id === destId);
      return {
        destination_id: destId,
        destination_name: dest?.name || '',
        topic_name: 'Manual Request',
        email_to: dest?.email || '',
        is_nextrequest: dest?.is_nextrequest || false,
        portal_url: dest?.portal_url || '',
        subject: '',
        // Seed the body with the plain-language description as a starting point.
        body: formData.natural_language_input || '',
        estimated_cost: 0,
      };
    });

    setGeneratedEmails(emails);
    setError('');
    setSuccess('');
    setActiveStep(2);
  };

  // Drag-and-drop state for topic combining
  const [draggedId, setDraggedId] = useState(null);
  const [dropTargetId, setDropTargetId] = useState(null);
  const [combining, setCombining] = useState(false);
  const [splitDialog, setSplitDialog] = useState({ open: false, topicId: null, instruction: '' });

  // Panel 2: Add a new topic manually
  const handleAddTopic = () => {
    const newTopic = {
      id: Date.now(),
      name: 'New Topic',
      description: 'Describe what records to request',
      destination_id: formData.destination_ids[0] || null,
    };
    setTopics([...topics, newTopic]);
  };

  // Panel 2: Delete a topic
  const handleDeleteTopic = (topicId) => {
    setTopics(topics.filter(t => t.id !== topicId));
  };

  // Panel 2: Edit topic
  const handleEditTopic = (topicId, field, value) => {
    setTopics(topics.map(t =>
      t.id === topicId ? { ...t, [field]: value } : t
    ));
  };

  // Panel 2: Drag-and-drop combine
  const handleDragStart = (e, topicId) => {
    setDraggedId(topicId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, topicId) => {
    e.preventDefault();
    if (topicId !== draggedId) {
      setDropTargetId(topicId);
      e.dataTransfer.dropEffect = 'move';
    }
  };

  const handleDragLeave = () => {
    setDropTargetId(null);
  };

  const handleDrop = async (e, targetId) => {
    e.preventDefault();
    if (!draggedId || draggedId === targetId) {
      setDraggedId(null);
      setDropTargetId(null);
      return;
    }

    const sourceTopics = topics.filter(t => t.id === draggedId || t.id === targetId);
    if (sourceTopics.length !== 2) return;

    setCombining(true);
    setDraggedId(null);
    setDropTargetId(null);

    try {
      const response = await praAPI.combineTopics(sourceTopics);
      const combined = response.data.topic;
      // Preserve the destination_id from the source topic
      combined.destination_id = sourceTopics[0].destination_id || formData.destination_ids[0];
      combined.id = Date.now();
      setTopics(prev => [
        ...prev.filter(t => t.id !== draggedId && t.id !== targetId),
        combined,
      ]);
    } catch (err) {
      setError('Failed to combine topics');
    } finally {
      setCombining(false);
    }
  };

  const handleDragEnd = () => {
    setDraggedId(null);
    setDropTargetId(null);
  };

  // Panel 2: Split topic
  const handleSplitTopic = async () => {
    const topic = topics.find(t => t.id === splitDialog.topicId);
    if (!topic) return;

    setCombining(true);
    try {
      const response = await praAPI.splitTopic(topic, splitDialog.instruction);
      const newTopics = response.data.topics.map((t, i) => ({
        ...t,
        id: Date.now() + i,
        destination_id: topic.destination_id || formData.destination_ids[0],
      }));
      setTopics(prev => [
        ...prev.filter(t => t.id !== splitDialog.topicId),
        ...newTopics,
      ]);
      setSplitDialog({ open: false, topicId: null, instruction: '' });
    } catch (err) {
      setError('Failed to split topic');
    } finally {
      setCombining(false);
    }
  };

  // Panel 2: Generate emails for all topics
  const handleGenerateEmails = async () => {
    if (topics.length === 0) {
      setError('Please add at least one topic');
      return;
    }

    setGeneratingEmails(true);
    setError('');
    setSuccess('');

    try {
      const response = await praAPI.generateEmails({
        destination_ids: formData.destination_ids,
        topics: topics,
        request_context: formData.request_context,
      });

      setGeneratedEmails(response.data.emails);
      setSuccess(`Generated ${response.data.emails.length} emails! Estimated cost: $${response.data.total_estimated_cost.toFixed(4)}`);
      setActiveStep(2);
    } catch (error) {
      console.error('Error generating emails:', error);
      setError(error.response?.data?.detail || 'Failed to generate emails');
    } finally {
      setGeneratingEmails(false);
    }
  };

  // Panel 3: Update generated email content
  const handleUpdateEmail = (emailIndex, field, value) => {
    const newEmails = [...generatedEmails];
    newEmails[emailIndex][field] = value;
    setGeneratedEmails(newEmails);
  };

  // Panel 3: Save draft and optionally send
  const handleSaveAndSend = async (emailIndex, send = false) => {
    const emailData = generatedEmails[emailIndex];

    setLoading(true);
    setError('');
    setSuccess('');
    setSnackbarOpen(false);

    try {
      // The first generated email continues the draft created via the quick-create
      // card (if any); any additional split-out topics become new requests.
      let requestId;
      if (emailIndex === 0 && draftId) {
        const updateResponse = await praAPI.updateRequest(draftId, {
          natural_language_input: emailData.topic_name,
          generated_request: emailData.body,
          subject: emailData.subject,
        });
        requestId = updateResponse.data.id;
      } else {
        const createResponse = await praAPI.createRequest({
          destination_id: emailData.destination_id,
          natural_language_input: emailData.topic_name,
          is_split_request: false,
          generated_request: emailData.body,
          subject: emailData.subject,
        });
        requestId = createResponse.data.id;
      }

      if (send) {
        // Open the shared sender-account chooser; the actual send happens from
        // the dialog once the user picks an account (handleSentFromDialog).
        setSendDialog({ open: true, requestId, emailIndex });
      } else {
        setSuccess('Draft saved successfully!');
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (error) {
      console.error('Error saving/sending:', error);
      const msg = error.response?.data?.detail || 'Operation failed';
      setError(msg);
      // Also surface as a snackbar so the failure is visible even when the user
      // is scrolled down in Panel 3 (the inline Alert sits at the top of the page).
      setSnackbarSeverity('error');
      setSnackbarMessage(msg);
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  // Called by the shared SenderAccountSendDialog after a successful send.
  const handleSentFromDialog = (emailIndex) => {
    const emailData = generatedEmails[emailIndex];
    setSentEmailIds([...sentEmailIds, emailIndex]);
    setSnackbarSeverity('success');
    setSnackbarMessage(`Email sent successfully to ${emailData?.destination_name || 'destination'}!`);
    setSnackbarOpen(true);
  };

  // Panel 3: Record a NextRequest portal submission
  const handleRecordNextRequest = async () => {
    const { emailIndex, nextrequest_id, nextrequest_url } = nextRequestDialog;
    const emailData = generatedEmails[emailIndex];

    setNextRequestDialog(prev => ({ ...prev, submitting: true }));
    setError('');

    try {
      // First save as draft to get request ID (reusing the quick-create draft for the first email)
      let requestId;
      if (emailIndex === 0 && draftId) {
        const updateResponse = await praAPI.updateRequest(draftId, {
          natural_language_input: emailData.topic_name,
          generated_request: emailData.body,
          subject: emailData.subject,
        });
        requestId = updateResponse.data.id;
      } else {
        const createResponse = await praAPI.createRequest({
          destination_id: emailData.destination_id,
          natural_language_input: emailData.topic_name,
          is_split_request: false,
          generated_request: emailData.body,
          subject: emailData.subject,
        });
        requestId = createResponse.data.id;
      }

      // Record the NextRequest submission
      await praAPI.submitViaNextRequest(requestId, {
        nextrequest_id,
        nextrequest_url,
      });

      setSentEmailIds([...sentEmailIds, emailIndex]);
      setNextRequestDialog({ open: false, emailIndex: null, nextrequest_id: '', nextrequest_url: '', submitting: false });
      setSnackbarMessage(`Recorded NextRequest submission for ${emailData.destination_name}!`);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to record submission');
      setNextRequestDialog(prev => ({ ...prev, submitting: false }));
    }
  };

  // Handle file uploads
  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    const uploadedFiles = files.map(file => ({
      filename: file.name,
      size: file.size,
      type: file.type,
    }));

    setFormData({
      ...formData,
      attached_files: [...formData.attached_files, ...uploadedFiles],
    });
  };

  // Handle destination selection
  const handleDestinationChange = (destinationId) => {
    const newIds = formData.destination_ids.includes(destinationId)
      ? formData.destination_ids.filter(id => id !== destinationId)
      : [...formData.destination_ids, destinationId];

    setFormData({
      ...formData,
      destination_ids: newIds,
    });
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        Create PRA Request
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Use AI to generate legally sound Public Records Act requests
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        <Step>
          <StepLabel>Request Overview</StepLabel>
        </Step>
        <Step>
          <StepLabel>Itemized Topics</StepLabel>
        </Step>
        <Step>
          <StepLabel>Generated Emails</StepLabel>
        </Step>
      </Stepper>

      {/* Panel 1: Request Overview */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          1. Request Overview
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Describe your request in plain language and attach supporting PDF files
        </Typography>

        {/* Destinations Multi-Select */}
        <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
          Select Destinations *
        </Typography>
        <FormGroup>
          {destinations.map((dest) => (
            <FormControlLabel
              key={dest.id}
              control={
                <Checkbox
                  checked={formData.destination_ids.includes(dest.id)}
                  onChange={() => handleDestinationChange(dest.id)}
                />
              }
              label={
                <span>
                  {dest.name}
                  {dest.is_nextrequest && (
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      (NextRequest Portal)
                    </Typography>
                  )}
                </span>
              }
            />
          ))}
        </FormGroup>

        {/* Request Context */}
        <TextField
          fullWidth
          label="Request Context (optional)"
          multiline
          rows={3}
          value={formData.request_context}
          onChange={(e) => setFormData({ ...formData, request_context: e.target.value })}
          placeholder="Background information about this investigation — who the parties are, what the story is, why these records matter. Gemini will use this to generate more targeted requests without quoting it verbatim in the letters."
          sx={{ mt: 3, mb: 2 }}
          helperText="Provide context about the investigation. This is passed to Gemini to improve precision but will not appear in the letters."
        />

        {/* Natural Language Input */}
        <TextField
          fullWidth
          label="Describe your request in plain language *"
          multiline
          rows={8}
          value={formData.natural_language_input}
          onChange={(e) => setFormData({ ...formData, natural_language_input: e.target.value })}
          placeholder="Example: I would like to request all emails between the Chancellor and the Police Chief regarding protest response from January 2025, as well as security expenditure records for TPUSA events at Zellerbach Hall..."
          sx={{ mb: 2 }}
        />

        {/* Manual mode toggle */}
        <FormControlLabel
          sx={{ mt: 2, mb: 0 }}
          control={
            <Switch
              checked={manualMode}
              onChange={(e) => setManualMode(e.target.checked)}
              color="primary"
            />
          }
          label="Write manually without AI"
        />
        {manualMode && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1 }}>
            Skip AI generation and write the request letter yourself. Your description above seeds the body as a starting point.
          </Typography>
        )}

        {/* File Upload */}
        <Box sx={{ mt: 2, mb: 3 }}>
          <input
            accept="application/pdf"
            style={{ display: 'none' }}
            id="file-upload"
            multiple
            type="file"
            onChange={handleFileUpload}
          />
          <label htmlFor="file-upload">
            <Button
              variant="outlined"
              component="span"
              startIcon={<Upload />}
            >
              Attach PDF Files
            </Button>
          </label>
          {formData.attached_files.length > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {formData.attached_files.length} file(s) attached
            </Typography>
          )}
        </Box>

        {/* Attached Files List */}
        {formData.attached_files.length > 0 && (
          <List dense>
            {formData.attached_files.map((file, idx) => (
              <ListItem key={idx}>
                <ListItemText
                  primary={file.filename}
                  secondary={`${(file.size / 1024).toFixed(2)} KB`}
                />
                <IconButton
                  edge="end"
                  size="small"
                  onClick={() => {
                    setFormData({
                      ...formData,
                      attached_files: formData.attached_files.filter((_, i) => i !== idx),
                    });
                  }}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </ListItem>
            ))}
          </List>
        )}

        <Button
          variant="contained"
          startIcon={manualMode ? <Edit /> : <AutoAwesome />}
          onClick={manualMode ? handleContinueManual : handleGenerateTopics}
          disabled={generatingTopics}
          fullWidth
          sx={{ mt: 2 }}
        >
          {generatingTopics
            ? <CircularProgress size={24} />
            : manualMode
              ? 'Continue to Manual Entry'
              : 'Generate Topics'}
        </Button>
      </Paper>

      {/* Panel 2: Generated Itemized Requests */}
      {activeStep >= 1 && !manualMode && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ fontFamily: 'Georgia, serif', color: '#00075e' }}>
                2. Itemized Topics
              </Typography>
              <Typography variant="body2" sx={{ color: '#838996', mt: 0.5 }}>
                {topics.length} topic{topics.length !== 1 ? 's' : ''} &middot; Each becomes a separate PRA request to avoid "undue burden" clauses
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                startIcon={<Add />}
                onClick={handleAddTopic}
                variant="outlined"
                size="small"
              >
                Add Topic
              </Button>
              <Button
                variant="contained"
                startIcon={generatingEmails ? <CircularProgress size={16} color="inherit" /> : <AutoAwesome />}
                onClick={handleGenerateEmails}
                disabled={generatingEmails || topics.length === 0}
                size="small"
              >
                Generate Emails
              </Button>
            </Box>
          </Box>

          {combining && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <CircularProgress size={14} sx={{ mr: 1 }} /> Processing with AI…
            </Alert>
          )}

          <Alert severity="info" variant="outlined" sx={{ mb: 2, fontSize: '0.8rem' }}>
            <strong>Tip:</strong> Drag one topic card onto another to merge them with AI. Use the Split button to divide a topic into parts.
          </Alert>

          {topics.length === 0 ? (
            <Alert severity="info">
              No topics yet. Add topics manually or generate them from your request description.
            </Alert>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
                gap: 2,
              }}
            >
              {topics.map((topic, idx) => {
                const isDragging = draggedId === topic.id;
                const isDropTarget = dropTargetId === topic.id && draggedId !== topic.id;
                return (
                  <Card
                    key={topic.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, topic.id)}
                    onDragOver={(e) => handleDragOver(e, topic.id)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, topic.id)}
                    onDragEnd={handleDragEnd}
                    elevation={isDropTarget ? 4 : 1}
                    sx={{
                      cursor: 'grab',
                      opacity: isDragging ? 0.4 : 1,
                      border: isDropTarget
                        ? '2px dashed #2a52be'
                        : '1px solid #e0ddd6',
                      backgroundColor: isDropTarget ? 'rgba(42,82,190,0.06)' : '#ffffff',
                      transition: 'all 0.15s ease',
                      position: 'relative',
                      '&:active': { cursor: 'grabbing' },
                    }}
                  >
                    <CardContent sx={{ pb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                        <DragIndicator
                          sx={{ color: '#c8c4bb', mr: 0.5, mt: 0.5, fontSize: 18, flexShrink: 0 }}
                        />
                        <Chip
                          label={idx + 1}
                          size="small"
                          sx={{
                            fontFamily: '"Helvetica Neue", Arial, sans-serif',
                            fontWeight: 700,
                            backgroundColor: '#2a52be',
                            color: '#fff',
                            mr: 1,
                            fontSize: '0.7rem',
                            height: 20,
                            flexShrink: 0,
                          }}
                        />
                        <TextField
                          value={topic.name}
                          onChange={(e) => handleEditTopic(topic.id, 'name', e.target.value)}
                          variant="standard"
                          fullWidth
                          placeholder="Topic name"
                          inputProps={{
                            style: {
                              fontFamily: 'Georgia, serif',
                              fontWeight: 700,
                              fontSize: '0.9rem',
                              color: '#00075e',
                            },
                          }}
                          InputProps={{ disableUnderline: false }}
                          onClick={(e) => e.stopPropagation()}
                          onMouseDown={(e) => e.stopPropagation()}
                        />
                      </Box>
                      <TextField
                        value={topic.description}
                        onChange={(e) => handleEditTopic(topic.id, 'description', e.target.value)}
                        variant="standard"
                        fullWidth
                        multiline
                        minRows={2}
                        placeholder="What records to request…"
                        inputProps={{
                          style: {
                            fontFamily: '"Helvetica Neue", Arial, sans-serif',
                            fontSize: '0.8rem',
                            color: '#838996',
                          },
                        }}
                        InputProps={{ disableUnderline: true }}
                        sx={{ mt: 0.5 }}
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                      />
                      {formData.destination_ids.length > 1 && (
                        <Box sx={{ mt: 1.5 }}>
                          <Select
                            value={topic.destination_id || formData.destination_ids[0] || ''}
                            onChange={(e) => handleEditTopic(topic.id, 'destination_id', e.target.value)}
                            size="small"
                            fullWidth
                            sx={{ fontSize: '0.75rem' }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {formData.destination_ids.map(destId => {
                              const dest = destinations.find(d => d.id === destId);
                              return dest ? (
                                <MenuItem key={dest.id} value={dest.id} sx={{ fontSize: '0.8rem' }}>
                                  {dest.name}
                                </MenuItem>
                              ) : null;
                            })}
                          </Select>
                        </Box>
                      )}
                    </CardContent>
                    <CardActions sx={{ pt: 0, px: 1.5, pb: 1, justifyContent: 'flex-end' }}>
                      <Tooltip title="Split this topic into parts">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSplitDialog({ open: true, topicId: topic.id, instruction: '' });
                          }}
                          sx={{ color: '#838996' }}
                        >
                          <CallSplit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete topic">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteTopic(topic.id);
                          }}
                          disabled={topics.length === 1}
                          sx={{ color: '#838996' }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </CardActions>

                    {isDropTarget && (
                      <Box
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'rgba(42,82,190,0.08)',
                          borderRadius: 'inherit',
                          pointerEvents: 'none',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#2a52be' }}>
                          <Merge />
                          <Typography sx={{ fontFamily: '"Helvetica Neue", Arial', fontWeight: 700, fontSize: '0.85rem', color: '#2a52be' }}>
                            Merge with AI
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Card>
                );
              })}
            </Box>
          )}

          {/* Split topic dialog */}
          <Dialog
            open={splitDialog.open}
            onClose={() => setSplitDialog({ open: false, topicId: null, instruction: '' })}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle sx={{ fontFamily: 'Georgia, serif', color: '#00075e' }}>
              Split Topic
            </DialogTitle>
            <DialogContent>
              <Typography variant="body2" sx={{ mb: 2, color: '#838996' }}>
                Describe how you want to split this topic. Gemini will create focused sub-topics based on your instruction.
              </Typography>
              {splitDialog.topicId && (
                <Typography variant="body2" sx={{ mb: 2, color: '#00075e', fontWeight: 600 }}>
                  Topic: {topics.find(t => t.id === splitDialog.topicId)?.name}
                </Typography>
              )}
              <TextField
                autoFocus
                fullWidth
                multiline
                rows={3}
                label="Split instruction"
                placeholder="e.g. Split into one topic for emails and another for financial records"
                value={splitDialog.instruction}
                onChange={(e) => setSplitDialog({ ...splitDialog, instruction: e.target.value })}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSplitDialog({ open: false, topicId: null, instruction: '' })}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSplitTopic}
                disabled={!splitDialog.instruction.trim() || combining}
                startIcon={combining ? <CircularProgress size={14} color="inherit" /> : <CallSplit />}
              >
                Split with AI
              </Button>
            </DialogActions>
          </Dialog>
        </Paper>
      )}

      {/* Panel 3: Generated Requests */}
      {activeStep >= 2 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            3. Generated Requests
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Review and edit each generated email before sending.
          </Typography>

          {generatedEmails.length === 0 ? (
            <Alert severity="info">
              No emails generated yet.
            </Alert>
          ) : (
            generatedEmails.map((email, idx) => {
              const isSent = sentEmailIds.includes(idx);
              const isNextRequest = email.is_nextrequest;
              return (
                <Paper
                  key={idx}
                  sx={{
                    p: 2,
                    mb: 3,
                    border: '1px solid',
                    borderColor: isSent ? 'success.main' : 'divider',
                    backgroundColor: isSent ? 'rgba(46,125,50,0.04)' : 'background.paper'
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontFamily: 'Georgia, serif', color: '#00075e' }}>
                        {email.destination_name} — {email.topic_name}
                      </Typography>
                      {isNextRequest && (
                        <Chip
                          icon={<Language sx={{ fontSize: 14 }} />}
                          label="NextRequest Portal"
                          size="small"
                          sx={{ mt: 0.5, backgroundColor: '#e8eaf6', color: '#3949ab', fontWeight: 600 }}
                        />
                      )}
                    </Box>
                    {isSent && (
                      <Box sx={{ display: 'flex', alignItems: 'center', color: 'success.main' }}>
                        <CheckCircle sx={{ mr: 0.5, fontSize: 20 }} />
                        <Typography variant="body2" color="success.main" fontWeight="bold">
                          Submitted
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {isNextRequest ? (
                    <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
                      This destination uses the <strong>NextRequest portal</strong>. Copy the request body below,
                      submit it at the portal, then record the confirmation number here.
                    </Alert>
                  ) : (
                    <TextField
                      fullWidth
                      label="To"
                      value={email.email_to || ''}
                      disabled
                      sx={{ mb: 2 }}
                    />
                  )}

                  <TextField
                    fullWidth
                    label="Subject"
                    value={email.subject}
                    onChange={(e) => handleUpdateEmail(idx, 'subject', e.target.value)}
                    sx={{ mb: 2 }}
                    disabled={isSent}
                  />

                  <TextField
                    fullWidth
                    multiline
                    rows={15}
                    label={isNextRequest ? 'Request Body (copy into portal)' : 'Email Body'}
                    value={email.body}
                    onChange={(e) => handleUpdateEmail(idx, 'body', e.target.value)}
                    sx={{ mb: 2 }}
                    disabled={isSent}
                  />

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      Estimated cost: ${email.estimated_cost?.toFixed(4)}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="outlined"
                        startIcon={<Save />}
                        onClick={() => handleSaveAndSend(idx, false)}
                        disabled={loading || isSent}
                      >
                        Save Draft
                      </Button>
                      {isNextRequest ? (
                        <>
                          {email.portal_url && (
                            <Button
                              variant="outlined"
                              startIcon={<OpenInNew />}
                              href={email.portal_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              disabled={isSent}
                            >
                              Open Portal
                            </Button>
                          )}
                          <Button
                            variant="contained"
                            color="primary"
                            startIcon={<CheckCircle />}
                            onClick={() => setNextRequestDialog({
                              open: true,
                              emailIndex: idx,
                              nextrequest_id: '',
                              nextrequest_url: '',
                              submitting: false,
                            })}
                            disabled={loading || isSent}
                          >
                            {isSent ? 'Submitted' : 'Record Submission'}
                          </Button>
                        </>
                      ) : (
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={<Send />}
                          onClick={() => handleSaveAndSend(idx, true)}
                          disabled={loading || isSent}
                        >
                          {loading && !isSent ? <CircularProgress size={20} /> : isSent ? 'Sent' : 'Send'}
                        </Button>
                      )}
                    </Box>
                  </Box>
                </Paper>
              );
            })
          )}
        </Paper>
      )}

      {/* NextRequest Submission Dialog */}
      <Dialog
        open={nextRequestDialog.open}
        onClose={() => !nextRequestDialog.submitting && setNextRequestDialog(prev => ({ ...prev, open: false }))}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontFamily: 'Georgia, serif', color: '#00075e' }}>
          Record NextRequest Submission
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: '#838996' }}>
            After submitting the request on the NextRequest portal, enter the confirmation details below
            so we can track it.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Request ID (e.g. 25-1234)"
            placeholder="25-1234"
            value={nextRequestDialog.nextrequest_id}
            onChange={(e) => setNextRequestDialog(prev => ({ ...prev, nextrequest_id: e.target.value }))}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Request URL (optional)"
            placeholder="https://cityofberkeleyca.nextrequest.com/requests/..."
            value={nextRequestDialog.nextrequest_url}
            onChange={(e) => setNextRequestDialog(prev => ({ ...prev, nextrequest_url: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setNextRequestDialog({ open: false, emailIndex: null, nextrequest_id: '', nextrequest_url: '', submitting: false })}
            disabled={nextRequestDialog.submitting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleRecordNextRequest}
            disabled={!nextRequestDialog.nextrequest_id.trim() || nextRequestDialog.submitting}
            startIcon={nextRequestDialog.submitting ? <CircularProgress size={14} color="inherit" /> : <CheckCircle />}
          >
            Record Submission
          </Button>
        </DialogActions>
      </Dialog>

      {/* App-wide sender-account chooser + send flow */}
      <SenderAccountSendDialog
        open={sendDialog.open}
        requestId={sendDialog.requestId}
        onClose={() => setSendDialog({ open: false, requestId: null, emailIndex: null })}
        onSent={(request) => handleSentFromDialog(sendDialog.emailIndex)}
      />

      {/* Snackbar for success/error notifications (top-right so it's visible
          regardless of scroll position within the multi-panel form) */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
          icon={snackbarSeverity === 'success'
            ? <CheckCircle fontSize="inherit" />
            : <Error fontSize="inherit" />}
        >
          <AlertTitle>{snackbarSeverity === 'success' ? 'Success' : 'Error'}</AlertTitle>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default CreateRequest;
